import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import type { AuthTokens, User, UserRole } from '@/types'

interface AuthContextValue {
  user: User | null
  tokens: AuthTokens | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  /** Store tokens received from signup / verify-email flows without re-calling the login endpoint. */
  storeTokens: (accessToken: string, refreshToken: string, tenantId: string) => void
  logout: () => void
  hasRole: (role: UserRole | UserRole[]) => boolean
  refreshTokens: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

const TOKEN_KEY = 'oc_access_token'
const REFRESH_KEY = 'oc_refresh_token'
const TENANT_KEY = 'oc_tenant_id'
const USER_KEY = 'oc_user'

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [tokens, setTokens] = useState<AuthTokens | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const stored = localStorage.getItem(USER_KEY)
    const accessToken = localStorage.getItem(TOKEN_KEY)
    if (stored && accessToken) {
      try {
        const parsedUser = JSON.parse(stored) as User
        setUser(parsedUser)
        setTokens({
          accessToken,
          refreshToken: localStorage.getItem(REFRESH_KEY) ?? '',
          expiresAt: new Date(Date.now() + 3600_000).toISOString(),
          tenantId: localStorage.getItem(TENANT_KEY) ?? '',
          user: parsedUser,
        })
      } catch {
        localStorage.removeItem(USER_KEY)
        localStorage.removeItem(TOKEN_KEY)
      }
    }
    setIsLoading(false)
  }, [])

  const login = useCallback(async (email: string, password: string): Promise<void> => {
    const res = await fetch('/api/v1/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Login failed' }))
      throw new Error(err.message ?? 'Login failed')
    }
    const data = (await res.json()) as AuthTokens
    localStorage.setItem(TOKEN_KEY, data.accessToken)
    localStorage.setItem(REFRESH_KEY, data.refreshToken)
    localStorage.setItem(TENANT_KEY, data.tenantId)
    localStorage.setItem(USER_KEY, JSON.stringify(data.user))
    setUser(data.user)
    setTokens(data)
  }, [])

  const storeTokens = useCallback((accessToken: string, refreshToken: string, tenantId: string) => {
    localStorage.setItem(TOKEN_KEY, accessToken)
    localStorage.setItem(REFRESH_KEY, refreshToken)
    localStorage.setItem(TENANT_KEY, tenantId)
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_KEY)
    localStorage.removeItem(TENANT_KEY)
    localStorage.removeItem(USER_KEY)
    setUser(null)
    setTokens(null)
  }, [])

  const hasRole = useCallback(
    (role: UserRole | UserRole[]): boolean => {
      if (!user) return false
      const roles = Array.isArray(role) ? role : [role]
      return roles.some((r) => user.roles.includes(r))
    },
    [user],
  )

  const refreshTokens = useCallback(async (): Promise<void> => {
    const refresh = localStorage.getItem(REFRESH_KEY)
    if (!refresh) return logout()
    const res = await fetch('/api/v1/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: refresh }),
    })
    if (!res.ok) return logout()
    const data = (await res.json()) as AuthTokens
    localStorage.setItem(TOKEN_KEY, data.accessToken)
    localStorage.setItem(REFRESH_KEY, data.refreshToken)
    setTokens(data)
  }, [logout])

  return (
    <AuthContext.Provider
      value={{
        user,
        tokens,
        isAuthenticated: !!user,
        isLoading,
        login,
        storeTokens,
        logout,
        hasRole,
        refreshTokens,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
